/**
 * What runs in the background of the technician's shell for the "Me" slice
 * (FM Technician rebuild R2: B16, B17, B18). Mounted once by AppLayout for the
 * technician shell; renders nothing but the PIN lock when it is locked.
 *
 *  - FIRST RUN (B17): a technician who has not finished the first run lands on
 *    /tech/welcome — once per app load, so they are never trapped there.
 *  - PIN LOCK (B17): with a PIN set, LOCK_AFTER_MS without a touch, or back
 *    after that long in the background (or with the page closed), shows the
 *    lock screen — at once after a reload, without waiting for the server.
 *  - OFFLINE PACK (B18): the saved pack is loaded from the phone at once, and
 *    refreshed on sign-in, on coming back to the foreground and when the
 *    connection returns (at most once a minute).
 *  - CONFLICTS (B18): a replay the server refuses (409/422) is also kept on the
 *    server (POST /tech/conflicts) — offlineSync.reportConflict.
 *  - P1 SOUND (B16): an urgent P1 alert arriving while the app is open plays
 *    the separate sound, when the setting is on. It watches the notification
 *    reads; a push arriving while the app is open makes the service worker
 *    ask for a re-read. Review R2-2: it also keeps the bell's own read going
 *    itself (the same key, so no second request), because it stays mounted
 *    while the lock is up — a P1 is heard on the lock screen too.
 */
import { useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { canOpenRoute } from '@/config/routeAccess';
import { useSignOut } from '@/hooks/useSignOut';
import { onReplayRefused } from '@/services/pendingQueue';
import { loadPack, refreshPack, reportConflict } from '@/services/offlineSync';
import { playP1Sound, primeP1Sound } from '@/services/p1Sound';
import { wantsP1Sound } from '@/utils/techAlertClass';
import PinLock, { setIdleLockOwner } from '@/components/Tech/PinLock';
import {
  awayTooLong, lastActive, LOCK_AFTER_MS, lockFlagSet, markActive, rememberPinSet, setLocked, storedPinSet,
  useTechLocked,
} from '@/components/Tech/lockState';
import { useTechProfile } from '@/components/Tech/techMeApi';

export const TECH_WELCOME = '/tech/welcome';
const ACTIVITY = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
const REFRESH_EVERY_MS = 60_000;
/** The last touch is written at most this often (scroll fires a lot). */
const ACTIVE_WRITE_EVERY_MS = 5_000;
/** The bell's first page (NotificationCentre's PAGE, Sidebar's BELL_LIMIT):
 *  the same query key, so this read is the bell's, not another one. */
export const NOTIFICATIONS_KEY = ['notifications', 20] as const;
export const NOTIFICATIONS_EVERY_MS = 30_000;

interface InboxRow {
  id: string; is_read: boolean; created_at?: string; event?: string | null; data?: Record<string, unknown> | null;
}

export default function TechMeEffects() {
  const user = useAuthStore((s) => s.user);
  const uid = user?.id ?? null;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const profile = useTechProfile(!!uid);
  const { signOut, signOutDialog } = useSignOut();

  // --- first run ----------------------------------------------------------
  const sentToWelcome = useRef(false);
  useEffect(() => {
    if (sentToWelcome.current || !profile.data || profile.data.onboarded_at) return;
    if (!canOpenRoute(user, TECH_WELCOME)) return;
    if (matchPath({ path: TECH_WELCOME, end: true }, pathname)) { sentToWelcome.current = true; return; }
    sentToWelcome.current = true;
    navigate(TECH_WELCOME, { replace: true });
  }, [profile.data, pathname, navigate, user]);

  // --- PIN lock -----------------------------------------------------------
  // Review R2-1: the lock does not wait for the server. pin_set is the
  // profile's answer when there is one, else the last answer this phone kept
  // (so a reload, offline or not, still locks); the last touch is kept too,
  // so time with the page closed counts toward LOCK_AFTER_MS.
  const serverPinSet = profile.data ? !!profile.data.pin_set : null;
  useEffect(() => {
    if (uid && serverPinSet !== null) rememberPinSet(uid, serverPinSet);
  }, [uid, serverPinSet]);
  const pinSet = serverPinSet ?? (uid ? storedPinSet(uid) === true : false);
  const locked = useTechLocked(uid);
  // Locked at render time (away too long with a PIN): keep the flag too, so
  // the lock holds whatever the clock says next.
  useEffect(() => {
    if (uid && locked && !lockFlagSet(uid)) setLocked(uid, true);
  }, [uid, locked]);
  useEffect(() => {
    if (!uid || !pinSet) return undefined;
    setIdleLockOwner(uid);
    return () => setIdleLockOwner(null);
  }, [uid, pinSet]);
  useEffect(() => {
    if (!uid || !pinSet || locked) return undefined;
    // Away too long already (closed, reloaded, phone asleep): lock now.
    if (awayTooLong(uid)) { setLocked(uid, true); return undefined; }
    let timer: number | undefined;
    let lastWrite = 0;
    const lock = () => setLocked(uid, true);
    // Review R2-2: the timer fires in EVERY tab, and the lock is shared. An
    // idle tab must not lock the one being used, so the timer locks only when
    // the shared last touch (any tab) is LOCK_AFTER_MS old; else it waits
    // out what is left of that.
    const due = () => {
      if (awayTooLong(uid)) { lock(); return; }
      const at = lastActive(uid) ?? Date.now();
      timer = window.setTimeout(due, Math.max(1_000, at + LOCK_AFTER_MS - Date.now()));
    };
    const arm = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(due, LOCK_AFTER_MS);
      const now = Date.now();
      if (now - lastWrite >= ACTIVE_WRITE_EVERY_MS) { lastWrite = now; markActive(uid, now); }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') return;
      if (awayTooLong(uid)) { lock(); return; }
      arm();
    };
    ACTIVITY.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    arm();
    return () => {
      if (timer) window.clearTimeout(timer);
      ACTIVITY.forEach((e) => window.removeEventListener(e, arm));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [uid, pinSet, locked]);

  // --- offline pack ---------------------------------------------------------
  useEffect(() => {
    if (!uid) return undefined;
    let last = 0;
    const refresh = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      if (Date.now() - last < REFRESH_EVERY_MS) return;
      last = Date.now();
      void refreshPack().catch(() => undefined);
    };
    void loadPack(uid);
    refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uid]);

  // --- conflicts ------------------------------------------------------------
  useEffect(() => {
    if (!uid) return undefined;
    onReplayRefused((item, detail, status) => { void reportConflict(item, detail, status); });
    return () => onReplayRefused(null);
  }, [uid]);

  // --- P1 sound -------------------------------------------------------------
  const p1On = profile.data?.alerts?.p1_sound !== false;
  useEffect(() => {
    // Sound needs a touch first (autoplay rules): the first one primes it.
    const prime = () => primeP1Sound();
    window.addEventListener('pointerdown', prime, { once: true, passive: true });
    return () => window.removeEventListener('pointerdown', prime);
  }, []);
  // The bell's read, kept going from here (R2-2): the page — and the bell —
  // are hidden under the lock, and nothing else would poll.
  useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => apiGet<{ notifications: InboxRow[]; unread_count: number }>(
      `/notifications/?limit=${NOTIFICATIONS_KEY[1]}`),
    refetchInterval: NOTIFICATIONS_EVERY_MS,
    enabled: !!uid,
  });
  const seen = useRef<Set<string>>(new Set());
  const since = useRef(Date.now());
  useEffect(() => {
    if (!uid) return undefined;
    const onRows = (rows: InboxRow[]) => {
      // An ARRIVAL: unread, not heard before, and created since the app opened
      // (what was already in the inbox, from any list, is not new).
      const fresh = rows.filter((r) => !r.is_read && !seen.current.has(r.id)
        && (!r.created_at || Date.parse(r.created_at) >= since.current - 5_000));
      fresh.forEach((r) => seen.current.add(r.id));
      if (p1On && fresh.some((r) => wantsP1Sound(r.event, r.data ?? null))) playP1Sound();
    };
    const unsub = qc.getQueryCache().subscribe((ev) => {
      if (ev.type !== 'updated' || ev.query.queryKey[0] !== 'notifications') return;
      const data = ev.query.state.data as { notifications?: InboxRow[]; pages?: { notifications?: InboxRow[] }[] } | undefined;
      const rows = data?.notifications ?? data?.pages?.flatMap((p) => p.notifications ?? []) ?? [];
      if (rows.length) onRows(rows);
    });
    // A push while the app is open: the service worker asks for a re-read.
    const onMessage = (e: MessageEvent) => {
      if ((e.data as { type?: string } | null)?.type === 'spaciora-push') {
        void qc.invalidateQueries({ queryKey: ['notifications'] });
      }
    };
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    sw?.addEventListener?.('message', onMessage);
    return () => { unsub(); sw?.removeEventListener?.('message', onMessage); };
  }, [uid, qc, p1On]);

  // Locked: AppLayout keeps the page mounted (unsaved work kept) but hidden,
  // inert and aria-hidden, and the lock is the top dialog — nothing readable
  // sits under it, for eyes or a screen reader.
  if (!uid || !locked) return signOutDialog;
  return (
    <>
      <PinLock
        onUnlocked={() => setLocked(uid, false)}
        // Review R2-1: no "unsent items" question from here — the lock screen
        // already says saved work stays (the queue is kept per person), and a
        // "Stay signed in" would have to leave the lock up anyway. The lock is
        // cleared by the sign-out itself (authStore.logout → clearLockState),
        // never before it.
        onUsePassword={() => { void signOut({ ask: false }); }}
      />
      {signOutDialog}
    </>
  );
}
